import { LocalNotifications } from "@capacitor/local-notifications"
import { Capacitor } from "@capacitor/core"
import { addDays, differenceInDays, parseISO } from "date-fns"

export interface SchedulableItem {
  type: "rent" | "lease" | "maintenance"
  id: string
  title: string
  body: string
  triggerDate: Date
}

/**
 * Request permission and schedule notifications for the next 30 days.
 * Cancels all previously scheduled notifications first to avoid duplicates.
 */
export async function scheduleReminders(items: SchedulableItem[]): Promise<void> {
  if (!Capacitor.isNativePlatform()) return

  const { display } = await LocalNotifications.requestPermissions()
  if (display !== "granted") return

  // Cancel all existing scheduled notifications
  const pending = await LocalNotifications.getPending()
  if (pending.notifications.length > 0) {
    await LocalNotifications.cancel({ notifications: pending.notifications })
  }

  const now = new Date()
  const cutoff = addDays(now, 30)

  const toSchedule = items
    .filter((item) => item.triggerDate > now && item.triggerDate <= cutoff)
    .map((item, i) => ({
      id: Math.abs(hashCode(item.type + item.id)) % 2147483647 || i + 1,
      title: item.title,
      body: item.body,
      schedule: { at: item.triggerDate },
      smallIcon: "ic_stat_icon_config_sample",
      sound: undefined,
    }))

  if (toSchedule.length > 0) {
    await LocalNotifications.schedule({ notifications: toSchedule })
  }
}

/**
 * Build reminder items from raw API data.
 * Call this after fetching leases and rent records from the API.
 */
export function buildReminderItems(data: {
  leases: Array<{ id: string; tenant_name?: string; end_date?: string | null }>
  rentRecords: Array<{ id: string; tenant_name?: string; due_date?: string | null; status?: string }>
  maintenanceRequests: Array<{ id: string; title?: string; status?: string; created_at?: string }>
}): SchedulableItem[] {
  const items: SchedulableItem[] = []
  const now = new Date()

  // Lease expiry reminders — 30 days and 7 days before end
  for (const lease of data.leases) {
    if (!lease.end_date) continue
    const endDate = parseISO(lease.end_date)
    const daysLeft = differenceInDays(endDate, now)
    if (daysLeft === 30 || daysLeft === 7) {
      items.push({
        type: "lease",
        id: lease.id,
        title: "Lease Expiring Soon",
        body: `${lease.tenant_name ?? "Tenant"}'s lease expires in ${daysLeft} days`,
        triggerDate: addDays(now, 1), // schedule for tomorrow morning
      })
    }
  }

  // Rent due reminders — 3 days before due date, unpaid only
  for (const record of data.rentRecords) {
    if (!record.due_date || record.status === "paid") continue
    const dueDate = parseISO(record.due_date)
    const daysUntilDue = differenceInDays(dueDate, now)
    if (daysUntilDue === 3) {
      items.push({
        type: "rent",
        id: record.id,
        title: "Rent Due in 3 Days",
        body: `${record.tenant_name ?? "Tenant"}'s rent is due on ${dueDate.toLocaleDateString()}`,
        triggerDate: addDays(now, 1),
      })
    }
  }

  // Maintenance overdue — open requests older than 7 days
  for (const req of data.maintenanceRequests) {
    if (!req.created_at || req.status === "completed" || req.status === "closed") continue
    const createdAt = parseISO(req.created_at)
    const daysOpen = differenceInDays(now, createdAt)
    if (daysOpen >= 7 && daysOpen % 7 === 0) {
      items.push({
        type: "maintenance",
        id: req.id,
        title: "Maintenance Request Open",
        body: `"${req.title ?? "Request"}" has been open for ${daysOpen} days`,
        triggerDate: addDays(now, 1),
      })
    }
  }

  return items
}

function hashCode(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = (Math.imul(31, hash) + str.charCodeAt(i)) | 0
  }
  return hash
}
